<template>
  <div class="survey-page survey-settings-page">
    <!-- Toolbar: title + tabs + save on one row -->
    <div class="ss-toolbar">
      <div class="ss-toolbar-left">
        <div class="survey-page-icon purple">
          <i class="bi bi-ui-checks"></i>
        </div>
        <div>
          <h5 class="survey-page-title">Survey Settings</h5>
          <p class="survey-page-sub">Configure forms for Online and Face-to-Face activities</p>
        </div>
      </div>

      <div class="survey-type-tabs ss-tabs">
        <button
          type="button"
          class="survey-type-tab"
          :class="{ active: activeType === 'online' }"
          @click="activeType = 'online'"
        >
          <i class="bi bi-camera-video-fill mr-1"></i> Online
        </button>
        <button
          type="button"
          class="survey-type-tab tab-f2f"
          :class="{ active: activeType === 'face-to-face' }"
          @click="activeType = 'face-to-face'"
        >
          <i class="bi bi-people-fill mr-1"></i> Face-to-Face
        </button>
      </div>

      <button class="survey-btn-primary ss-save" :disabled="saving" @click="save">
        <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
        <i v-else class="bi bi-check2 mr-1"></i>
        Save
      </button>
    </div>

    <div v-if="loading" class="survey-loading">
      <div class="spinner-border spinner-border-sm text-primary mr-2"></div> Loading settings...
    </div>

    <div v-else class="ss-body">
      <!-- Row 1: General + Timing/Options — equal columns -->
      <div class="ss-row ss-row-top">
        <div class="survey-panel">
          <h6 class="survey-panel-title">
            <i class="bi bi-sliders"></i>
            {{ activeTabLabel }} survey
          </h6>
          <div class="form-group">
            <label class="survey-field-label">Survey title</label>
            <input v-model="form.title" class="form-control sv-input" placeholder="e.g. Post Activity Survey" />
          </div>
          <div class="form-group mb-0">
            <label class="survey-field-label">Description shown to participants</label>
            <textarea v-model="form.description" class="form-control sv-input" rows="3" placeholder="Brief intro text..." />
          </div>
        </div>

        <div class="survey-panel">
          <h6 class="survey-panel-title"><i class="bi bi-clock"></i> Timing & options</h6>

          <label class="ss-open-toggle" :class="{ 'is-open': form.is_active, 'is-closed': !form.is_active }">
            <input v-model="form.is_active" type="checkbox" />
            <span class="ss-open-switch" aria-hidden="true">
              <i class="bi" :class="form.is_active ? 'bi-unlock-fill' : 'bi-lock-fill'"></i>
            </span>
            <span class="ss-open-copy">
              <strong>{{ form.is_active ? 'Survey open' : 'Survey closed' }}</strong>
              <small>{{ form.is_active ? 'Participants can submit responses' : 'Participants cannot open or submit this survey type' }}</small>
            </span>
          </label>

          <div class="ss-inline-fields">
            <div class="form-group">
              <label class="survey-field-label">Link expires (hrs after activity end)</label>
              <input v-model.number="form.expiry_hours_after_end" type="number" min="1" max="168" class="form-control sv-input" />
            </div>
            <div class="form-group">
              <label class="survey-field-label">Rating scale max</label>
              <input v-model.number="form.rating_scale_max" type="number" min="3" max="10" class="form-control sv-input" />
            </div>
          </div>
          <div class="ss-options-grid">
            <label class="survey-toggle-card" :class="{ 'is-on': form.allow_anonymous }">
              <input v-model="form.allow_anonymous" type="checkbox" />
              <span>Anonymous</span>
            </label>
            <label class="survey-toggle-card" :class="{ 'is-on': form.require_comment }">
              <input v-model="form.require_comment" type="checkbox" />
              <span>Require comments</span>
            </label>
            <label class="survey-toggle-card" :class="{ 'is-on': form.one_response_per_ip }">
              <input v-model="form.one_response_per_ip" type="checkbox" />
              <span>One per browser</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Row 2: Questions + Preview — balanced columns -->
      <div class="ss-row ss-row-bottom">
        <div class="survey-panel ss-questions-panel">
          <div class="panel-head-row">
            <h6 class="survey-panel-title mb-0">
              <i class="bi bi-list-check"></i>
              Questions
            </h6>
            <button type="button" class="survey-btn-ghost" @click="addQuestion">
              <i class="bi bi-plus-lg mr-1"></i> Add question
            </button>
          </div>

          <div v-if="!form.questions.length" class="q-empty">
            No questions yet. Add at least one rating or text question.
          </div>

          <div v-for="(q, idx) in form.questions" :key="q.id" class="q-card">
            <div class="q-card-top">
              <span class="q-badge">Q{{ idx + 1 }}</span>
              <div class="q-type-pills">
                <button type="button" class="q-type-pill" :class="{ active: q.type === 'rating' }" @click="q.type = 'rating'">Rating</button>
                <button type="button" class="q-type-pill" :class="{ active: q.type === 'text' }" @click="q.type = 'text'">Text</button>
              </div>
              <label class="q-req-toggle">
                <input v-model="q.required" type="checkbox" />
                Required
              </label>
              <button v-if="form.questions.length > 1" type="button" class="q-remove" title="Remove" @click="removeQuestion(idx)">
                <i class="bi bi-trash3"></i>
              </button>
            </div>
            <input v-model="q.label" class="form-control sv-input" placeholder="Question label" />
          </div>
        </div>

        <div class="ss-preview-col">
          <div class="preview-card" :class="activeType === 'online' ? 'preview-online' : 'preview-f2f'">
            <div class="preview-label">Participant preview</div>
            <div class="preview-badge">{{ activeTabLabel }}</div>
            <div class="preview-title">{{ form.title || 'Survey title' }}</div>
            <p class="preview-desc">{{ form.description || 'Description will appear here.' }}</p>

            <div v-for="(q, idx) in form.questions.slice(0, 3)" :key="q.id" class="preview-q">
              <div class="preview-q-label">{{ idx + 1 }}. {{ q.label || 'Question' }}</div>
              <div v-if="q.type === 'rating'" class="preview-rating-demo">
                <span v-for="n in Math.min(form.rating_scale_max || 5, 5)" :key="n" class="demo-dot">{{ n }}</span>
              </div>
              <div v-else class="preview-text-placeholder">Text response...</div>
            </div>
            <div v-if="form.questions.length > 3" class="preview-more">
              +{{ form.questions.length - 3 }} more question{{ form.questions.length - 3 === 1 ? '' : 's' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "@/css/surveys.css";
import axios from "axios";

const emptyForm = () => ({
  title: "",
  description: "",
  expiry_hours_after_end: 5,
  rating_scale_max: 5,
  questions: [],
  allow_anonymous: true,
  require_comment: false,
  one_response_per_ip: true,
  is_active: true,
});

export default {
  name: "SurveySettings",
  data() {
    return {
      loading: true,
      saving: false,
      activeType: "online",
      settingsByType: {
        online: emptyForm(),
        "face-to-face": emptyForm(),
      },
    };
  },
  computed: {
    form: {
      get() { return this.settingsByType[this.activeType]; },
      set(val) { this.settingsByType[this.activeType] = val; },
    },
    activeTabLabel() {
      return this.activeType === "online" ? "Online" : "Face-to-Face";
    },
  },
  mounted() { this.load(); },
  methods: {
    mapSettings(data) {
      return {
        title: data.title,
        description: data.description || "",
        expiry_hours_after_end: data.expiry_hours_after_end,
        rating_scale_max: data.rating_scale_max,
        questions: Array.isArray(data.questions) ? data.questions.map((q) => ({ ...q })) : [],
        allow_anonymous: !!data.allow_anonymous,
        require_comment: !!data.require_comment,
        one_response_per_ip: !!data.one_response_per_ip,
        is_active: !!data.is_active,
      };
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get("api/survey-settings");
        this.settingsByType.online = this.mapSettings(data.online || {});
        this.settingsByType["face-to-face"] = this.mapSettings(data["face-to-face"] || {});
      } catch (e) {
        toastr.error("Failed to load survey settings.", "Error");
      } finally {
        this.loading = false;
      }
    },
    addQuestion() {
      this.form.questions.push({ id: `q_${Date.now()}`, type: "rating", label: "", required: false });
    },
    removeQuestion(idx) { this.form.questions.splice(idx, 1); },
    async save() {
      this.saving = true;
      try {
        await axios.put("api/survey-settings", { type: this.activeType, ...this.form });
        toastr.success(`${this.activeTabLabel} survey settings saved.`, "Saved");
      } catch (e) {
        const msg = e.response?.data?.message
          || (e.response?.data?.errors ? Object.values(e.response.data.errors).flat().join(" ") : "Failed to save.");
        toastr.error(msg, "Error");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.survey-settings-page {
  width: 100%;
}

/* Full-width toolbar */
.ss-toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding-bottom: 1.25rem;
  margin-bottom: 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 21, 0.08);
}

.ss-toolbar-left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex: 1;
  min-width: 200px;
}

.ss-tabs {
  margin-bottom: 0;
}

.ss-save {
  flex-shrink: 0;
}

/* Body rows */
.ss-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ss-row {
  display: grid;
  gap: 1rem;
}

.ss-row-top {
  grid-template-columns: 1fr 1fr;
}

.ss-row-bottom {
  grid-template-columns: 1.4fr 1fr;
  align-items: stretch;
}

@media (max-width: 991.98px) {
  .ss-row-top,
  .ss-row-bottom {
    grid-template-columns: 1fr;
  }
}

.ss-inline-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.ss-open-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.9rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid rgba(0, 0, 21, 0.1);
  border-radius: 12px;
  background: #f8f9fb;
  cursor: pointer;
  user-select: none;
}

.ss-open-toggle input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.ss-open-switch {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
  background: rgba(229, 83, 83, 0.12);
  color: #e55353;
}

.ss-open-toggle.is-open .ss-open-switch {
  background: rgba(43, 182, 163, 0.15);
  color: #1f8f7f;
}

.ss-open-toggle.is-open {
  border-color: rgba(43, 182, 163, 0.35);
  background: rgba(43, 182, 163, 0.06);
}

.ss-open-toggle.is-closed {
  border-color: rgba(229, 83, 83, 0.28);
  background: rgba(229, 83, 83, 0.05);
}

.ss-open-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ss-open-copy strong {
  font-size: 0.9rem;
  color: #2b3a55;
}

.ss-open-copy small {
  font-size: 0.75rem;
  color: #768192;
  line-height: 1.35;
}

.ss-options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

@media (max-width: 575.98px) {
  .ss-inline-fields,
  .ss-options-grid {
    grid-template-columns: 1fr;
  }
}

.ss-questions-panel {
  margin-bottom: 0;
  height: 100%;
}

.ss-preview-col {
  display: flex;
  flex-direction: column;
}

.preview-card {
  border-radius: 14px;
  padding: 1.25rem;
  color: #fff;
  flex: 1;
  min-height: 100%;
}

.preview-online { background: linear-gradient(145deg, #4d8dff 0%, #3a6fd4 100%); }
.preview-f2f { background: linear-gradient(145deg, #2bb6a3 0%, #1f9a8a 100%); }

.preview-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.8;
  margin-bottom: 0.5rem;
}

.preview-badge {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.15rem 0.5rem;
  border-radius: 6px;
  margin-bottom: 0.5rem;
}

.preview-title {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.35rem;
}

.preview-desc {
  font-size: 0.8rem;
  opacity: 0.9;
  margin-bottom: 1rem;
  line-height: 1.45;
}

.preview-q {
  margin-bottom: 0.85rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.preview-q:last-of-type { border-bottom: none; }

.preview-q-label {
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 0.4rem;
  opacity: 0.95;
}

.preview-rating-demo { display: flex; gap: 0.35rem; }

.demo-dot {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
}

.preview-text-placeholder {
  font-size: 0.75rem;
  opacity: 0.6;
  font-style: italic;
  padding: 0.4rem 0.6rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.preview-more {
  font-size: 0.72rem;
  opacity: 0.7;
  margin-top: 0.5rem;
}

.panel-head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.sv-input {
  border-radius: 10px;
  border-color: rgba(0, 0, 21, 0.1);
  font-size: 0.88rem;
}

.q-empty {
  text-align: center;
  padding: 1.5rem;
  color: #9aa5b4;
  font-size: 0.85rem;
  border: 1px dashed rgba(0, 0, 21, 0.1);
  border-radius: 10px;
}

.q-card {
  border: 1px solid rgba(0, 0, 21, 0.08);
  border-radius: 12px;
  padding: 0.85rem;
  margin-bottom: 0.65rem;
  background: #fafbfc;
}

.q-card-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
  flex-wrap: wrap;
}

.q-badge {
  font-size: 0.72rem;
  font-weight: 700;
  color: #4d8dff;
  background: rgba(77, 141, 255, 0.12);
  padding: 0.15rem 0.5rem;
  border-radius: 6px;
}

.q-type-pills { display: flex; gap: 0.25rem; }

.q-type-pill {
  border: 1px solid rgba(0, 0, 21, 0.1);
  background: #fff;
  border-radius: 6px;
  padding: 0.15rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #768192;
  cursor: pointer;
}

.q-type-pill.active {
  background: #4d8dff;
  border-color: #4d8dff;
  color: #fff;
}

.q-req-toggle {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.78rem;
  color: #768192;
  margin: 0 0 0 auto;
  cursor: pointer;
}

.q-remove {
  background: none;
  border: none;
  color: #e55353;
  padding: 0.2rem;
  cursor: pointer;
  font-size: 0.9rem;
}
</style>
